import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { keys } from '@blimo/api-client';
import { useApi } from '@blimo/api-client/react';
import { propertyEditSchema } from '@blimo/shared';
import {
  applyProblemToForm,
  Button,
  CheckboxField,
  Form,
  FormRootError,
  InputField,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  TextareaField,
  toast,
} from '@blimo/ui';
import { ReadOnlyHint } from '../../components/data.jsx';
import { PartyPicker } from '../meters/party-picker.jsx';
import { PropertyImageField } from './image-field.jsx';

/**
 * Edits the BLIMO-owned fields of a property (PATCH /admin/properties/{id}).
 * @param {{ property: any, open: boolean, onOpenChange: (open: boolean) => void }} props
 */
export function PropertyEditSheet({ property, open, onOpenChange }) {
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Liegenschaft bearbeiten</SheetTitle>
          <SheetDescription>{property.name}</SheetDescription>
        </SheetHeader>
        {open && <PropertyEditForm property={property} onDone={() => onOpenChange(false)} />}
      </SheetContent>
    </Sheet>
  );
}

function PropertyEditForm({ property, onDone }) {
  const api = useApi();
  const queryClient = useQueryClient();
  const form = useForm({
    resolver: zodResolver(propertyEditSchema),
    defaultValues: {
      description: property.description ?? '',
      is_in_preparation: Boolean(property.is_in_preparation),
      manager_party_id: property.manager?.id ? String(property.manager.id) : '',
      contact_party_id: property.contact?.id ? String(property.contact.id) : '',
      latitude: property.latitude != null ? String(Number(property.latitude)) : '',
      longitude: property.longitude != null ? String(Number(property.longitude)) : '',
    },
  });
  const mutation = useMutation({
    mutationFn: (values) => {
      const coord = (/** @type {string|undefined} */ v) => (v && v.trim() !== '' ? Number(v.trim().replace(',', '.')) : null);
      const payload = { description: values.description?.trim() || null, is_in_preparation: values.is_in_preparation };
      // G-18: Verwalter/Ansprechpartner nur senden, wenn geändert
      for (const field of ['manager_party_id', 'contact_party_id']) {
        if (form.formState.dirtyFields[field]) payload[field] = values[field] ? Number(values[field]) : null;
      }
      // Koordinaten nur senden, wenn geändert (sonst bliebe die Herkunft „geokodiert“ erhalten)
      if (form.formState.dirtyFields.latitude || form.formState.dirtyFields.longitude) {
        Object.assign(payload, { latitude: coord(values.latitude), longitude: coord(values.longitude) });
      }
      return api.admin.properties.update(property.id, payload);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(keys.admin.detail('properties', property.id), updated);
      queryClient.invalidateQueries({ queryKey: ['blimo', 'admin', 'properties', 'list'] });
      queryClient.invalidateQueries({ queryKey: keys.admin.dashboard() });
      toast.success('Liegenschaft gespeichert');
      onDone();
    },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await mutation.mutateAsync(values);
    } catch (error) {
      applyProblemToForm(error, form.setError);
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="flex flex-1 flex-col gap-4 px-4" noValidate>
        <ReadOnlyHint>Name und Adresse werden aus WinCasa übernommen und können hier nicht geändert werden.</ReadOnlyHint>
        <FormRootError />
        <PropertyImageField property={property} />
        <CheckboxField
          name="is_in_preparation"
          label="Vorbereitungsmodus"
          description="Unterdrückt Benachrichtigungen an Eigentümer (z. B. bei neuen Dokumenten), solange die Liegenschaft vorbereitet wird."
        />
        <Controller
          name="manager_party_id"
          control={form.control}
          render={({ field }) => (
            <PartyPicker
              label="Verwalter (Organisation)"
              typeFilter="organization"
              value={field.value}
              valueLabel={property.manager?.display_name}
              emptyLabel="Kein Verwalter"
              onChange={(id) => field.onChange(id)}
            />
          )}
        />
        <Controller
          name="contact_party_id"
          control={form.control}
          render={({ field }) => (
            <PartyPicker
              label="Ansprechpartner"
              value={field.value}
              valueLabel={property.contact?.display_name}
              emptyLabel="Kein Ansprechpartner"
              onChange={(id) => field.onChange(id)}
            />
          )}
        />
        <TextareaField name="description" label="Beschreibung" rows={8} description="Interne Beschreibung der Liegenschaft (max. 5.000 Zeichen)." />
        <fieldset className="flex flex-col gap-2">
          <legend className="text-sm font-medium">Koordinaten (Dezimalgrad)</legend>
          <div className="grid grid-cols-2 gap-3">
            <InputField name="latitude" label="Breitengrad" inputMode="decimal" placeholder="z. B. 51.9606" autoComplete="off" />
            <InputField name="longitude" label="Längengrad" inputMode="decimal" placeholder="z. B. 7.6261" autoComplete="off" />
          </div>
          <p className="text-xs text-muted-foreground">
            Manuell eingetragene Koordinaten werden bei der automatischen Ermittlung nicht überschrieben. Beide Felder leeren entfernt die Koordinaten.
          </p>
        </fieldset>
        <SheetFooter className="px-0">
          <Button type="submit" loading={form.formState.isSubmitting}>
            Speichern
          </Button>
          <Button type="button" variant="outline" onClick={onDone}>
            Abbrechen
          </Button>
        </SheetFooter>
      </form>
    </Form>
  );
}
